import React, { useState, useRef, useEffect } from 'react';
import { Mail, Users, PlusCircle, CheckCircle, ExternalLink, MoreHorizontal, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchWithAuth = async (url, opts = {}) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, { ...opts, headers: { ...opts.headers, Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const ACTIONS = [
  { id: 'outreach', label: 'Quick Outreach', icon: Mail, color: 'text-[#D4AF37]' },
  { id: 'decision_makers', label: 'Find Decision Makers', icon: Users, color: 'text-blue-600' },
  { id: 'pipeline', label: 'Add to Pipeline', icon: PlusCircle, color: 'text-emerald-600' },
  { id: 'contacted', label: 'Mark Contacted', icon: CheckCircle, color: 'text-indigo-600' },
  { id: 'crm', label: 'Save to CRM', icon: ExternalLink, color: 'text-purple-600' },
];

export const CompanyActionMenu = ({ companyName, companyData, onOutreach, onStatusChange }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleAction = async (actionId, e) => {
    e.stopPropagation();
    const encoded = encodeURIComponent(companyName);

    if (actionId === 'outreach') {
      setOpen(false);
      onOutreach?.({ company: companyName, ...companyData });
      return;
    }

    setLoading(actionId);
    try {
      if (actionId === 'decision_makers') {
        const result = await fetchWithAuth(`${API_URL}/api/intelligence/company/${encoded}/decision-makers`);
        if (result?.error) {
          toast.error(`Decision-maker search unavailable: ${result.error}`);
        } else {
          const count = result?.contacts?.length || 0;
          toast.success(count > 0 ? `Found ${count} decision maker${count > 1 ? 's' : ''} at ${companyName}` : `No decision makers found yet for ${companyName}`);
        }
        onStatusChange?.('decision_makers', result);
      } else if (actionId === 'pipeline') {
        await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/add-to-pipeline`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ company: companyName }),
        });
        toast.success(`${companyName} added to pipeline`);
        onStatusChange?.('pipeline', { added: true });
      } else if (actionId === 'contacted') {
        await fetchWithAuth(`${API_URL}/api/intelligence/company/${encoded}/outreach`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'already_contacted' }),
        });
        toast.success(`${companyName} marked as contacted`);
        onStatusChange?.('contacted', { status: 'already_contacted' });
      } else if (actionId === 'crm') {
        await fetchWithAuth(`${API_URL}/api/intelligence/company/${encoded}/outreach`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'ready', crm_flagged: true }),
        });
        toast.success(`${companyName} flagged for CRM sync`);
        onStatusChange?.('crm', { flagged: true });
      }
    } catch (err) {
      const msg = err.message === '409' ? `${companyName} is already in the pipeline` : `Action failed — try again`;
      if (err.message === '409') toast.info(msg); else toast.error(msg);
    } finally {
      setLoading(null);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid={`action-menu-${companyName?.replace(/\s+/g, '-')?.toLowerCase()}`}
      >
        <MoreHorizontal className="h-4 w-4 text-gray-400" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl py-1 w-52"
          data-testid={`action-dropdown-${companyName?.replace(/\s+/g, '-')?.toLowerCase()}`}>
          <div className="px-3 py-1.5 border-b border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Actions</p>
          </div>
          {ACTIONS.map(a => (
            <button key={a.id} onClick={(e) => handleAction(a.id, e)}
              disabled={loading === a.id}
              className="w-full px-3 py-2 text-left text-xs flex items-center gap-2.5 hover:bg-gray-50 transition-colors disabled:opacity-50"
              data-testid={`action-${a.id}-${companyName?.replace(/\s+/g, '-')?.toLowerCase()}`}>
              {loading === a.id
                ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                : <a.icon className={`h-3.5 w-3.5 ${a.color}`} />}
              <span className="text-gray-700">{a.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
